import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "description", "disabled", "error", "legend", "optional", "required", "size"];
import PropTypes from 'prop-types';
import React, { forwardRef, useRef } from 'react';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from '../FormField/FormField.context';
import { StyledFieldSet } from './FieldSet.styles';

/**
 * @extends FormField
 * @related [`LabeledControl`]
 * @subcomponents ['Legend']
 * @since 8.0.0
 */
export var FieldSet = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    description = _ref.description,
    disabled = _ref.disabled,
    error = _ref.error,
    legend = _ref.legend,
    optional = _ref.optional,
    required = _ref.required,
    size = _ref.size,
    rest = _objectWithoutProperties(_ref, _excluded);
  // Generate an id to link FormHelp to the FieldSet
  var descriptionId = useRef(identifier()).current;

  // Context for nested Legend and FormField components
  var context = {
    optional: optional,
    required: required,
    disabled: disabled
  };
  if (description) {
    context.descriptionId = descriptionId;
  }
  if (typeof size !== 'undefined') {
    context.size = size;
  }
  if (typeof error !== 'undefined') {
    context.error = error;
  }
  return /*#__PURE__*/React.createElement(StyledFieldSet, _extends({
    ref: forwardedRef,
    "aria-describedby": context.descriptionId
  }, rest), /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, legend, children, description));
});
FieldSet.displayName = 'FieldSet';
FieldSet.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A group of thematically related controls, usually `LabeledControl`.
   */
  children: PropTypes.node.isRequired,
  /**
   * A node that describes the field set, typically a
   * `FormHelp`. This will automatically associate the
   * `FormHelp` with the field set using
   * [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute).
   */
  description: PropTypes.node,
  /**
   * Displays the field set in a disabled state. Inherited by the nested form controls.
   */
  disabled: PropTypes.bool,
  /**
   * A `Legend` used to caption the group of controls.
   */
  legend: PropTypes.node.isRequired,
  /**
   * Indicates the field set is required. Inherited by the nested form controls and legend.
   */
  required: PropTypes.bool,
  /**
   * Indicates the field set is optional in the legend.
   */
  optional: PropTypes.bool,
  /**
   * Sets the `size` for all nested form controls.
   */
  size: PropTypes.oneOf(['md', 'lg']),
  /**
   * Displays the field set in an error state. Inherited by the nested form controls and
   * FormHelp text.
   */
  error: PropTypes.bool
} : {};